<template>
  <ion-page>
    <ion-header :translucent="true">
      <ion-toolbar class="custom-toolbar">
        <ion-buttons slot="start">
          <ion-menu-button color="light"></ion-menu-button>
        </ion-buttons>
        <ion-title class="custom-title">HR Attendance</ion-title>
        <ion-buttons slot="end">
          <ion-button @click="refreshData" color="light" class="refresh-btn">
            <ion-icon slot="icon-only" :icon="refreshOutline"></ion-icon>
          </ion-button>
        </ion-buttons>
      </ion-toolbar>
    </ion-header>

    <ion-content :fullscreen="true" class="attendance-content">
      <div class="attendance-container">
        
        <!-- Live Clock Bento Widget -->
        <div class="clock-widget">
          <div class="clock-icon-wrapper">
            <ion-icon :icon="timeOutline" class="clock-icon"></ion-icon>
          </div>
          <div class="clock-time">{{ liveTime }}</div>
          <div class="clock-date">{{ liveDate }}</div>
        </div>

        <!-- Geofence & Location Widget -->
        <div class="location-widget" :class="geofenceClass">
          <div class="widget-header">
            <ion-icon :icon="pinOutline" class="location-icon"></ion-icon>
            <h3>Geofence Status</h3>
          </div>
          <div class="widget-body">
            <div v-if="gpsLoading" class="gps-status-loading">
              <ion-spinner name="crescent" color="primary"></ion-spinner>
              <span>Acquiring GPS coordinates...</span>
            </div>
            <div v-else-if="gpsError" class="gps-status-error">
              <span class="error-badge">GPS Error</span>
              <p>{{ gpsError }}</p>
              <ion-button size="small" fill="outline" color="danger" class="ion-margin-top" @click="acquireLocation">Retry GPS</ion-button>
            </div>
            <div v-else class="gps-status-resolved">
              <div class="status-indicator">
                <span class="pulse-dot" :class="isInsideGeofence ? 'green' : 'amber'"></span>
                <span class="status-text">{{ isInsideGeofence ? 'Within Office Geofence' : 'Outside Office Geofence' }}</span>
              </div>
              <p class="office-name" v-if="config && config.office">
                Office: <strong>{{ config.office.name }}</strong>
              </p>
              <p class="distance-text" v-if="distanceToOffice !== null">
                Distance: <strong>{{ formatDistance(distanceToOffice) }}</strong>
                <span v-if="config && config.office"> (Target: &lt; {{ config.office.radius_meters }}m)</span>
              </p>
            </div>
          </div>
        </div>

        <!-- Today's Status Timeline Widget -->
        <div class="timeline-widget">
          <div class="widget-header">
            <ion-icon :icon="checkmarkCircleOutline" class="timeline-icon"></ion-icon>
            <h3>Today's Logs</h3>
          </div>
          <div class="timeline-container">
            <!-- Check-In Node -->
            <div class="timeline-item" :class="{ active: todayLogs.check_in }">
              <div class="timeline-badge" :class="{ 'check-in-active': todayLogs.check_in }">
                <ion-icon :icon="logInOutline"></ion-icon>
              </div>
              <div class="timeline-content">
                <div class="content-header">
                  <h4>Check In</h4>
                  <ion-badge v-if="todayLogs.status" :color="getStatusColor(todayLogs.status)" class="status-badge">
                    {{ todayLogs.status }}
                  </ion-badge>
                </div>
                <p v-if="todayLogs.check_in" class="time-stamp">{{ todayLogs.check_in }}</p>
                <p v-else class="time-empty">Not Checked In yet</p>
              </div>
            </div>
            
            <!-- Check-Out Node -->
            <div class="timeline-item" :class="{ active: todayLogs.check_out }">
              <div class="timeline-badge" :class="{ 'check-out-active': todayLogs.check_out }">
                <ion-icon :icon="logOutOutline"></ion-icon>
              </div>
              <div class="timeline-content">
                <h4>Check Out</h4>
                <p v-if="todayLogs.check_out" class="time-stamp">{{ todayLogs.check_out }}</p>
                <p v-else class="time-empty">Not Checked Out yet</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Field Visit Form Controller -->
        <div class="action-panel-card">
          <div class="form-item-toggle">
            <div class="toggle-info">
              <span class="toggle-title">Field Visit / Off-site Work</span>
              <span class="toggle-subtitle">Toggle if working outside the office geofence</span>
            </div>
            <ion-toggle v-model="isFieldVisit" color="primary" @ionChange="onFieldVisitChange"></ion-toggle>
          </div>

          <transition name="fade-slide">
            <div v-if="isFieldVisit" class="field-purpose-wrapper">
              <ion-item class="purpose-input-item" lines="none">
                <ion-icon :icon="documentTextOutline" slot="start" class="purpose-icon"></ion-icon>
                <ion-label position="floating" class="custom-label">Field Visit Purpose <span class="required">*</span></ion-label>
                <ion-input v-model="fieldVisitPurpose" placeholder="e.g. client meeting, site inspection" class="custom-input"></ion-input>
              </ion-item>
              <div class="reporting-boss-tip" v-if="config && config.reporting_boss">
                <ion-icon :icon="personOutline" class="boss-icon"></ion-icon>
                <span>Requires verification by: <strong>{{ config.reporting_boss }}</strong></span>
              </div>
            </div>
          </transition>

          <!-- Attendance Action Buttons -->
          <div class="action-buttons-container">
            <button 
              class="btn-attendance btn-in" 
              :disabled="todayLogs.check_in && !todayLogs.check_out"
              @click="performAttendance('in')"
            >
              <ion-icon :icon="logInOutline" class="btn-icon"></ion-icon>
              <span>Check In</span>
            </button>
            
            <button 
              class="btn-attendance btn-out" 
              :disabled="!todayLogs.check_in || todayLogs.check_out"
              @click="performAttendance('out')"
            >
              <ion-icon :icon="logOutOutline" class="btn-icon"></ion-icon>
              <span>Check Out</span>
            </button>
          </div>
        </div>

        <!-- Accordion Info Widget -->
        <div class="info-accordion-card">
          <div class="accordion-header" @click="showInfo = !showInfo">
            <div class="header-left">
              <ion-icon :icon="briefcaseOutline" class="briefcase-icon"></ion-icon>
              <span>My Shift & Schedule</span>
            </div>
            <ion-icon :icon="showInfo ? chevronUpOutline : chevronDownOutline" class="chevron-icon"></ion-icon>
          </div>
          
          <transition name="slide">
            <div v-if="showInfo" class="accordion-body">
              <div v-if="loadingConfig" class="accordion-loading">
                <ion-spinner name="dots"></ion-spinner>
              </div>
              <div v-else-if="config" class="schedule-details">
                <div class="detail-row">
                  <span class="detail-label">Employee Type</span>
                  <span class="detail-value">{{ config.employee_type === 'full_time' ? 'Full Time' : 'Part Time' }}</span>
                </div>
                <div class="detail-row" v-if="config.office">
                  <span class="detail-label">Standard Office Hours</span>
                  <span class="detail-value">{{ formatTime(config.office.office_start_time) }} - {{ formatTime(config.office.office_end_time) }}</span>
                </div>
                <div class="detail-row" v-if="config.shift">
                  <span class="detail-label">Shift Name</span>
                  <span class="detail-value">{{ config.shift.name }}</span>
                </div>
                <div class="detail-row" v-if="config.shift">
                  <span class="detail-label">Shift Hours</span>
                  <span class="detail-value">{{ formatTime(config.shift.start_time) }} - {{ formatTime(config.shift.end_time) }}</span>
                </div>
                <div class="detail-row">
                  <span class="detail-label">Grace Time</span>
                  <span class="detail-value">{{ config.shift ? config.shift.grace_time_minutes : (config.office ? config.office.grace_time_minutes : 0) }} Minutes</span>
                </div>
                <div class="detail-row">
                  <span class="detail-label">Reporting Manager</span>
                  <span class="detail-value">{{ config.reporting_boss || 'Top Management' }}</span>
                </div>
              </div>
            </div>
          </transition>
        </div>

      </div>
    </ion-content>
  </ion-page>
</template>

<script>
/* eslint-disable */
import { 
  IonButtons, 
  IonContent, 
  IonHeader, 
  IonMenuButton, 
  IonPage, 
  IonTitle, 
  IonToolbar, 
  IonCard, 
  IonCardContent, 
  IonItem, 
  IonLabel, 
  IonToggle, 
  IonInput, 
  IonButton, 
  IonIcon, 
  IonBadge, 
  IonSpinner, 
  alertController 
} from '@ionic/vue';
import { 
  timeOutline, 
  pinOutline, 
  checkmarkCircleOutline, 
  closeCircleOutline, 
  briefcaseOutline, 
  personOutline, 
  businessOutline, 
  logInOutline, 
  logOutOutline, 
  documentTextOutline, 
  refreshOutline, 
  chevronUpOutline, 
  chevronDownOutline 
} from 'ionicons/icons';
import { Geolocation } from '@capacitor/geolocation';
import Loading from '../../Loading';
import { pushFocus } from '../../pushNotifications';

export default {
  name: 'Attendance',
  // Field visit verified / rejected notifications open this screen: refresh today's status.
  mixins: [pushFocus],
  components: {
    IonButtons,
    IonContent,
    IonHeader,
    IonMenuButton,
    IonPage,
    IonTitle,
    IonToolbar,
    IonCard,
    IonCardContent,
    IonItem,
    IonLabel,
    IonToggle,
    IonInput,
    IonButton,
    IonIcon,
    IonBadge,
    IonSpinner
  },
  data() {
    return {
      // Icons
      timeOutline,
      pinOutline,
      checkmarkCircleOutline,
      closeCircleOutline,
      briefcaseOutline,
      personOutline,
      businessOutline,
      logInOutline,
      logOutOutline,
      documentTextOutline,
      refreshOutline,
      chevronUpOutline,
      chevronDownOutline,

      // App Config & Schedule
      config: null,
      loadingConfig: true,
      showInfo: false,

      // Live Clock Data
      liveTime: '--:--:--',
      liveDate: 'Loading current date...',
      clockInterval: null,

      // GPS Tracking
      gpsLoading: true,
      gpsError: null,
      currentLat: null,
      currentLng: null,
      distanceToOffice: null,
      isInsideGeofence: false,
      locationInterval: null,

      // Attendance Today Logs
      todayLogs: {
        check_in: null,
        check_out: null,
        status: null,
        notes: null
      },

      // Field Visit Form Model
      isFieldVisit: false,
      fieldVisitPurpose: '',
    }
  },
  computed: {
    geofenceClass() {
      if (this.gpsLoading) return 'loading';
      if (this.gpsError) return 'error';
      return this.isInsideGeofence ? 'inside' : 'outside';
    }
  },
  created() {
    this.startClock();
    this.refreshData();
    // Auto-update coordinates every 30 seconds while on this screen
    this.locationInterval = setInterval(() => {
      this.acquireLocation();
    }, 30000);
  },
  beforeUnmount() {
    if (this.clockInterval) clearInterval(this.clockInterval);
    if (this.locationInterval) clearInterval(this.locationInterval);
  },
  methods: {
    reloadForPush() {
      this.refreshData();
    },
    startClock() {
      const updateClock = () => {
        const now = new Date();
        this.liveTime = now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true });
        this.liveDate = now.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric', year: 'numeric' });
      };
      updateClock();
      this.clockInterval = setInterval(updateClock, 1000);
    },
    formatTime(timeStr) {
      if (!timeStr) return '';
      try {
        const parts = timeStr.split(':');
        const hours = parseInt(parts[0], 10);
        const minutes = parts[1];
        const ampm = hours >= 12 ? 'PM' : 'AM';
        const formattedHours = hours % 12 || 12;
        return `${formattedHours}:${minutes} ${ampm}`;
      } catch (e) {
        return timeStr;
      }
    },
    formatDistance(meters) {
      if (meters === null) return '';
      if (meters < 1000) {
        return `${Math.round(meters)}m`;
      }
      return `${(meters / 1000).toFixed(2)}km`;
    },
    getStatusColor(status) {
      if (!status) return 'medium';
      const s = status.toLowerCase();
      if (s.includes('present')) return 'success';
      if (s.includes('late')) return 'warning';
      if (s.includes('leave') || s.includes('holiday') || s.includes('off')) return 'tertiary';
      if (s.includes('field')) return 'primary';
      return 'danger';
    },
    async presentAlert(title, message) {
      const alert = await alertController.create({
        header: title,
        message: message,
        buttons: ['OK'],
      });
      await alert.present();
    },
    onFieldVisitChange() {
      if (!this.isFieldVisit) {
        this.fieldVisitPurpose = '';
      }
    },
    refreshData() {
      this.fetchUserConfig();
      this.fetchTodayLogs();
      this.acquireLocation();
    },
    async fetchUserConfig() {
      this.loadingConfig = true;
      const data = {
        username: window.localStorage.username,
        password: window.localStorage.password,
      };

      this.$http.post('hr/getUserConfig', data, { skipLoading: true })
        .then(response => {
          if (response.data.status) {
            this.config = response.data.config;
            this.checkGeofence();
          }
          this.loadingConfig = false;
        })
        .catch(err => {
          this.loadingConfig = false;
        });
    },
    async fetchTodayLogs() {
      const todayStr = new Date().toISOString().split('T')[0];
      const monthStr = todayStr.substring(0, 7); // YYYY-MM
      
      const data = {
        username: window.localStorage.username,
        password: window.localStorage.password,
        month: monthStr
      };

      this.$http.post('hr/myAttendanceHistory', data, { skipLoading: true })
        .then(response => {
          if (response.data.status && response.data.history) {
            const todayItem = response.data.history.find(h => h.date === todayStr);
            if (todayItem) {
              this.todayLogs.check_in = todayItem.check_in;
              this.todayLogs.check_out = todayItem.check_out;
              
              // Map overall status to readable label
              if (todayItem.status !== 'Absent' && todayItem.status !== 'Off Day' && todayItem.status !== 'Holiday') {
                this.todayLogs.status = todayItem.status;
              } else {
                this.todayLogs.status = null;
              }
            } else {
              this.todayLogs.check_in = null;
              this.todayLogs.check_out = null;
              this.todayLogs.status = null;
            }
          }
        })
        .catch(err => {
          // Silent catch
        });
    },
    async acquireLocation() {
      this.gpsLoading = true;
      this.gpsError = null;

      try {
        const coordinates = await Geolocation.getCurrentPosition({
          enableHighAccuracy: true,
          timeout: 10000
        });

        this.currentLat = coordinates.coords.latitude;
        this.currentLng = coordinates.coords.longitude;
        this.gpsLoading = false;
        this.checkGeofence();
      } catch (e) {
        this.gpsLoading = false;
        this.gpsError = 'Failed to acquire location. Make sure GPS permissions are allowed.';
        this.distanceToOffice = null;
        this.isInsideGeofence = false;
      }
    },
    checkGeofence() {
      if (this.currentLat === null || this.currentLng === null || !this.config || !this.config.office) {
        return;
      }

      const office = this.config.office;
      const dist = this.calculateDistance(
        this.currentLat,
        this.currentLng,
        parseFloat(office.latitude),
        parseFloat(office.longitude)
      );

      this.distanceToOffice = dist;
      this.isInsideGeofence = dist <= parseInt(office.radius_meters, 10);

      // Force field visit to false if inside the office geofence.
      // Do not auto-toggle it to true if outside; let the user control it.
      if (this.isInsideGeofence) {
        this.isFieldVisit = false;
      }
    },
    calculateDistance(lat1, lon1, lat2, lon2) {
      const earthRadius = 6371000; // in meters
      const dLat = (lat2 - lat1) * Math.PI / 180;
      const dLon = (lon2 - lon1) * Math.PI / 180;
      const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
                Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
                Math.sin(dLon / 2) * Math.sin(dLon / 2);
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
      return earthRadius * c;
    },
    isLateCheckIn() {
      if (this.isFieldVisit) return false;
      if (!this.config) return false;
      const shift = this.config.shift;
      const office = this.config.office;
      let startTimeStr = null;
      let graceMinutes = 0;
      
      if (shift && shift.start_time) {
        startTimeStr = shift.start_time;
        graceMinutes = parseInt(shift.grace_time_minutes, 10) || 0;
      } else if (office && office.office_start_time) {
        startTimeStr = office.office_start_time;
        graceMinutes = parseInt(office.grace_time_minutes, 10) || 0;
      }
      
      if (!startTimeStr) return false;
      
      const now = new Date();
      const [hours, minutes, seconds] = startTimeStr.split(':').map(Number);
      const thresholdTime = new Date(now.getFullYear(), now.getMonth(), now.getDate(), hours, minutes, seconds || 0);
      thresholdTime.setMinutes(thresholdTime.getMinutes() + graceMinutes);
      
      return now > thresholdTime;
    },
    isEarlyCheckOut() {
      if (!this.config) return false;
      const shift = this.config.shift;
      const office = this.config.office;
      let endTimeStr = null;
      
      if (shift && shift.end_time) {
        endTimeStr = shift.end_time;
      } else if (office && office.office_end_time) {
        endTimeStr = office.office_end_time;
      }
      
      if (!endTimeStr) return false;
      
      const now = new Date();
      const [hours, minutes, seconds] = endTimeStr.split(':').map(Number);
      const endTime = new Date(now.getFullYear(), now.getMonth(), now.getDate(), hours, minutes, seconds || 0);
      
      return now < endTime;
    },
    async performAttendance(type) {
      if (type === 'in' && this.isFieldVisit && !this.fieldVisitPurpose.trim()) {
        this.presentAlert('Validation Error', 'Field visit purpose is required.');
        return;
      }
      
      try {
        const coordinates = await Geolocation.getCurrentPosition({
          enableHighAccuracy: true,
          timeout: 10000
        });

        if (type === 'in' && this.isLateCheckIn()) {
          const alert = await alertController.create({
            header: 'Late Check-in Reason',
            message: 'You are checking in late. Please enter a reason:',
            inputs: [
              {
                name: 'reason',
                type: 'text',
                placeholder: 'Reason (required)',
              }
            ],
            buttons: [
              {
                text: 'Cancel',
                role: 'cancel'
              },
              {
                text: 'Submit',
                handler: (data) => {
                  if (!data.reason || !data.reason.trim()) {
                    this.presentAlert('Validation Error', 'Reason for late check-in is required.');
                    return false;
                  }
                  this.submitAttendance(type, coordinates, data.reason);
                }
              }
            ]
          });
          await alert.present();
          return;
        }

        if (type === 'out' && this.isEarlyCheckOut()) {
          const alert = await alertController.create({
            header: 'Early Checkout Reason',
            message: 'You are checking out early. Please enter a reason:',
            inputs: [
              {
                name: 'reason',
                type: 'text',
                placeholder: 'Reason (required)',
              }
            ],
            buttons: [
              {
                text: 'Cancel',
                role: 'cancel'
              },
              {
                text: 'Submit',
                handler: (data) => {
                  if (!data.reason || !data.reason.trim()) {
                    this.presentAlert('Validation Error', 'Reason for early checkout is required.');
                    return false;
                  }
                  this.submitAttendance(type, coordinates, data.reason);
                }
              }
            ]
          });
          await alert.present();
          return;
        }

        // Standard flow
        this.submitAttendance(type, coordinates, null);

      } catch (e) {
        this.presentAlert('Location Error', 'Unable to retrieve location. Please make sure location services are enabled.');
      }
    },
    submitAttendance(type, coordinates, reason) {
      const endpoint = type === 'in' ? 'hr/checkIn' : 'hr/checkOut';
      const payload = {
        username: window.localStorage.username,
        password: window.localStorage.password,
        latitude: coordinates.coords.latitude,
        longitude: coordinates.coords.longitude,
        device_info: navigator.userAgent,
        is_field_visit: this.isFieldVisit ? 1 : 0,
        field_visit_purpose: this.isFieldVisit ? this.fieldVisitPurpose : null,
      };

      if (type === 'in' && reason) {
        payload.late_reason = reason;
      } else if (type === 'out' && reason) {
        payload.early_checkout_reason = reason;
      }

      this.$http.post(endpoint, payload)
        .then(response => {
          if (response.data.status) {
            this.presentAlert('Success', response.data.message);
            this.fieldVisitPurpose = '';
            this.isFieldVisit = false;
            this.refreshData(); // Reload config, logs and GPS status immediately
          } else {
            this.presentAlert('Failed', response.data.message);
          }
        })
        .catch(err => {
          // Managed globally by interceptors
        });
    }
  }
}
</script>

<style scoped>
/* Core Styles */
.attendance-content {
  --background: #f4f6fc;
}

.attendance-container {
  padding: 16px;
  max-width: 600px;
  margin: 0 auto;
}

/* Custom Toolbar Styles */
.custom-toolbar {
  --background: linear-gradient(135deg, #102a43 0%, #243b53 100%);
  color: #fff;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.custom-title {
  font-weight: 700;
  letter-spacing: 0.5px;
}

.refresh-btn {
  margin-right: 8px;
}

/* Bento Live Clock Card */
.clock-widget {
  background: linear-gradient(135deg, #ffffff 0%, #f0f4f8 100%);
  border-radius: 20px;
  padding: 24px 16px;
  text-align: center;
  box-shadow: 0 10px 30px rgba(16, 42, 67, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.8);
  position: relative;
  overflow: hidden;
  margin-bottom: 16px;
}

.clock-widget::before {
  content: '';
  position: absolute;
  top: -50px;
  right: -50px;
  width: 150px;
  height: 150px;
  background: radial-gradient(circle, rgba(72, 187, 120, 0.1) 0%, rgba(255, 255, 255, 0) 70%);
  border-radius: 50%;
}

.clock-icon-wrapper {
  display: inline-flex;
  background: rgba(16, 42, 67, 0.05);
  border-radius: 50%;
  padding: 10px;
  margin-bottom: 8px;
}

.clock-icon {
  font-size: 24px;
  color: #102a43;
}

.clock-time {
  font-size: 38px;
  font-weight: 800;
  color: #102a43;
  letter-spacing: -1px;
  margin-bottom: 4px;
  font-family: monospace, system-ui;
}

.clock-date {
  font-size: 14px;
  font-weight: 600;
  color: #627d98;
}

/* Location and Geofence Widget */
.location-widget {
  background: #ffffff;
  border-radius: 20px;
  padding: 20px;
  box-shadow: 0 10px 30px rgba(16, 42, 67, 0.08);
  border-left: 6px solid #bcccdc;
  margin-bottom: 16px;
  transition: all 0.3s ease;
}

.location-widget.loading {
  border-left-color: #3b82f6;
}

.location-widget.error {
  border-left-color: #ef4444;
  background: #fff8f8;
}

.location-widget.inside {
  border-left-color: #10b981;
  background: linear-gradient(to right, #f0fdf4, #ffffff);
}

.location-widget.outside {
  border-left-color: #f59e0b;
  background: linear-gradient(to right, #fffbeb, #ffffff);
}

.widget-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 14px;
}

.location-icon {
  font-size: 20px;
  color: #486581;
}

.widget-header h3 {
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: #102a43;
}

.gps-status-loading {
  display: flex;
  align-items: center;
  gap: 12px;
  color: #486581;
  font-size: 14px;
}

.gps-status-error {
  color: #b7791f;
}

.error-badge {
  background-color: #fecaca;
  color: #b91c1c;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 700;
}

.gps-status-resolved {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.status-indicator {
  display: flex;
  align-items: center;
  gap: 8px;
}

.pulse-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  display: inline-block;
}

.pulse-dot.green {
  background-color: #10b981;
  box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.4);
  animation: pulse-green 2s infinite;
}

.pulse-dot.amber {
  background-color: #f59e0b;
  box-shadow: 0 0 0 0 rgba(245, 158, 11, 0.4);
  animation: pulse-amber 2s infinite;
}

@keyframes pulse-green {
  0% {
    transform: scale(0.95);
    box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.7);
  }
  70% {
    transform: scale(1);
    box-shadow: 0 0 0 6px rgba(16, 185, 129, 0);
  }
  100% {
    transform: scale(0.95);
    box-shadow: 0 0 0 0 rgba(16, 185, 129, 0);
  }
}

@keyframes pulse-amber {
  0% {
    transform: scale(0.95);
    box-shadow: 0 0 0 0 rgba(245, 158, 11, 0.7);
  }
  70% {
    transform: scale(1);
    box-shadow: 0 0 0 6px rgba(245, 158, 11, 0);
  }
  100% {
    transform: scale(0.95);
    box-shadow: 0 0 0 0 rgba(245, 158, 11, 0);
  }
}

.status-text {
  font-size: 15px;
  font-weight: 700;
  color: #102a43;
}

.office-name, .distance-text {
  margin: 2px 0 0 0;
  font-size: 13px;
  color: #486581;
}

/* Timeline Logs Widget */
.timeline-widget {
  background: #ffffff;
  border-radius: 20px;
  padding: 20px;
  box-shadow: 0 10px 30px rgba(16, 42, 67, 0.08);
  margin-bottom: 16px;
}

.timeline-icon {
  font-size: 20px;
  color: #486581;
}

.timeline-container {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 14px;
  position: relative;
  padding-left: 12px;
}

.timeline-container::before {
  content: '';
  position: absolute;
  top: 16px;
  left: 23px;
  bottom: 16px;
  width: 2px;
  background-color: #e4e7eb;
}

.timeline-item {
  display: flex;
  gap: 16px;
  position: relative;
  align-items: center;
}

.timeline-badge {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-color: #f0f4f8;
  color: #9fb3c8;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  z-index: 1;
  border: 2px solid #ffffff;
  box-shadow: 0 0 0 2px #e4e7eb;
}

.timeline-badge.check-in-active {
  background-color: #10b981;
  color: #ffffff;
  box-shadow: 0 0 0 2px #10b981;
}

.timeline-badge.check-out-active {
  background-color: #ef4444;
  color: #ffffff;
  box-shadow: 0 0 0 2px #ef4444;
}

.timeline-content {
  flex: 1;
  background-color: #f8fafc;
  border-radius: 12px;
  padding: 10px 14px;
  border: 1px solid #e2e8f0;
}

.timeline-item.active .timeline-content {
  background-color: #ffffff;
  box-shadow: 0 4px 12px rgba(16, 42, 67, 0.03);
}

.content-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.timeline-content h4 {
  margin: 0;
  font-size: 14px;
  font-weight: 700;
  color: #334e68;
}

.time-stamp {
  margin: 4px 0 0 0;
  font-size: 16px;
  font-weight: 800;
  color: #102a43;
}

.time-empty {
  margin: 4px 0 0 0;
  font-size: 13px;
  color: #829ab1;
  font-style: italic;
}

.status-badge {
  font-size: 11px;
  padding: 3px 8px;
  font-weight: 700;
}

/* Action Control Card */
.action-panel-card {
  background: #ffffff;
  border-radius: 20px;
  padding: 20px;
  box-shadow: 0 10px 30px rgba(16, 42, 67, 0.08);
  margin-bottom: 16px;
}

.form-item-toggle {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 12px;
  border-bottom: 1px dashed #e4e7eb;
}

.toggle-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.toggle-title {
  font-size: 15px;
  font-weight: 700;
  color: #102a43;
}

.toggle-subtitle {
  font-size: 12px;
  color: #627d98;
}

/* Field Purpose Styles */
.field-purpose-wrapper {
  margin-top: 14px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.purpose-input-item {
  --background: #f8fafc;
  --padding-start: 12px;
  --border-radius: 12px;
  border: 1px solid #cbd5e1;
  border-radius: 12px;
}

.purpose-icon {
  color: #627d98;
  margin-right: 8px;
}

.custom-label {
  color: #486581;
  font-weight: 600;
  font-size: 13px;
}

.custom-input {
  --color: #102a43;
  font-size: 14px;
}

.required {
  color: #ef4444;
}

.reporting-boss-tip {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #627d98;
  font-size: 12px;
  background-color: #f0f4f8;
  padding: 8px 12px;
  border-radius: 8px;
}

.boss-icon {
  font-size: 14px;
  color: #486581;
}

/* Action Attendance Buttons */
.action-buttons-container {
  display: flex;
  gap: 14px;
  margin-top: 20px;
}

.btn-attendance {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border: none;
  border-radius: 14px;
  padding: 16px;
  font-size: 16px;
  font-weight: 700;
  color: #ffffff;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}

.btn-attendance:active {
  transform: scale(0.96);
}

.btn-attendance:disabled {
  background: #e2e8f0 !important;
  color: #94a3b8 !important;
  box-shadow: none;
  cursor: not-allowed;
}

.btn-in {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  box-shadow: 0 4px 14px rgba(16, 185, 129, 0.3);
}

.btn-out {
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  box-shadow: 0 4px 14px rgba(239, 68, 68, 0.3);
}

.btn-icon {
  font-size: 20px;
}

/* Accordion Info Card */
.info-accordion-card {
  background: #ffffff;
  border-radius: 20px;
  box-shadow: 0 10px 30px rgba(16, 42, 67, 0.08);
  overflow: hidden;
  margin-bottom: 24px;
}

.accordion-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  cursor: pointer;
  background-color: #f8fafc;
  transition: background-color 0.2s ease;
}

.accordion-header:active {
  background-color: #f1f5f9;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 10px;
  font-weight: 700;
  color: #334e68;
}

.briefcase-icon {
  font-size: 20px;
  color: #486581;
}

.chevron-icon {
  font-size: 18px;
  color: #627d98;
}

.accordion-body {
  padding: 16px 20px;
  border-top: 1px solid #f1f5f9;
}

.accordion-loading {
  display: flex;
  justify-content: center;
  padding: 10px;
}

.schedule-details {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.detail-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 13.5px;
}

.detail-label {
  color: #627d98;
  font-weight: 500;
}

.detail-value {
  color: #102a43;
  font-weight: 700;
}

/* Vue Animations */
.slide-enter-active, .slide-leave-active {
  transition: max-height 0.3s ease-out, opacity 0.2s ease;
  max-height: 300px;
  overflow: hidden;
}

.slide-enter-from, .slide-leave-to {
  max-height: 0;
  opacity: 0;
}

.fade-slide-enter-active, .fade-slide-leave-active {
  transition: all 0.3s ease;
}

.fade-slide-enter-from, .fade-slide-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}
</style>
